/**
 * The server-backed artifact-persistence admission seam.
 *
 * Generic Application Ontology Program — lane C2b, Stage 4.
 *
 * WHAT THIS COMPOSES. `createStudioApplicationArtifactPersistenceServerSeam()`
 * turns the four Stage-3 server legs into the ONE port
 * `registerStudioTools()` accepts (`applicationArtifactPersistence`):
 *
 *   1. GET  …/application-ontology-generation/attempt-binding
 *      — the Stage-1 persisted `{series, attempt}`, closed-parsed with the
 *        SAME shape guards the server-side persisted-record parser uses.
 *   2. POST …/application-evidence/observations/artifact-validation
 *      — the observation append, at the server-owned receipt boundary.
 *   3. POST …/application-evidence/eligibilities/artifact-persistence
 *      — issuance, through the SEALED browser client
 *        (`requestArtifactPersistenceEligibility`), which the sealed
 *        admission order calls itself.
 *   4. POST …/application-evidence/verifications/artifact-persistence
 *      — verification, answered with the canonical imported decision.
 *
 * The order is NOT re-implemented here: `admit()` hands the four legs to the
 * sealed `admitOntologyGeneratedApplicationPersistence()`, whose ordered
 * constant is the one legal sequence. This module adds no step, skips no
 * step, and decides nothing — every verdict it returns is the verifier's.
 *
 * BOUND-NESS IS THE ARTIFACT'S OWN FACT. `admit()` asks the resolver port
 * for the artifact's ATTACHED bound evidence — a pure, synchronous, in-memory
 * read (the resolver's type forbids transport: it is not async). UNBOUND
 * (nothing attached) answers `null` with ZERO network — the ordinary lane's
 * only observable delta is the one already-resolved awaited promise the A1
 * seam-2 comment describes. A present-but-MALFORMED attachment is REFUSED
 * (`ADMISSION_UNAVAILABLE`), never dropped: silently treating it as unbound
 * would make a corrupted bound generation indistinguishable from one that
 * was never bound at all.
 *
 * THE GATE IS SERVER-DERIVED (recorded ledger deviation). There is no
 * browser-side config read and no build-time flag — a browser cannot read
 * `ctx.env`, and a second config authority would drift from the server's
 * real one. When the five-value seam config is absent the routes answer 503,
 * the first leg fails closed, and `admit()` refuses `ADMISSION_UNAVAILABLE`.
 *
 * THE ATTACHMENT CONTRACT (consumer-first, producer pending). This module is
 * the READ side of `metadata[APPLICATION_ARTIFACT_BOUND_EVIDENCE_ATTACHMENT_KEY]`;
 * the WRITE side — the generation completion path attaching the bundle to a
 * bound artifact — does not exist yet, so every production admit() today
 * resolves UNBOUND. That direction is deliberate (derive the gate from the
 * consumer): the closed parse below is the contract a future producer must
 * satisfy, not a description of one that already writes.
 *
 * WHY THE HEAVY LEGS ARE DYNAMICALLY IMPORTED. This module is statically
 * imported by the production bridge bootstrap (`installStudioBridge()`), the
 * same bootstrap that deliberately `await import()`s `register-studio-tools`
 * to keep the entry chunk lean. The sealed admission client (and through it
 * the invariant evaluator and canonical hashing) therefore loads only on the
 * first BOUND admit — the unbound path touches none of it.
 */
import type {
  ApplicationArtifactPersistenceAdmissionDecision,
  ApplicationArtifactPersistenceAdmissionRequest,
  ApplicationAttemptBinding,
  ApplicationEvidenceAdmissionRefusalCode,
  ApplicationSeriesBinding,
} from '@cogo/acceptance-contracts'
import type { ApplicationArtifactValidationObservation } from '@cogo/schema/application-artifact-validation-observation'
import type { ContentRef } from '@cogo/publisher-core'

import type { LoadedApp } from '@/features/page-editor/store/types'
import type { StudioApplicationArtifactPersistenceServerSeam } from '@/mcp/studio-tool-registration-options'

import type { StudioArtifactValidationObservationAppendResult } from './application-evidence-admission-client'
import {
  hasExactApplicationEvidenceKeys,
  isApplicationAttemptBindingShape,
  isApplicationEvidenceContentRef,
  isApplicationSeriesBindingShape,
} from './application-ontology-generation'
import type { ObserveOntologyGeneratedApplicationInput } from './observe-generated-application'

// ─── The Studio routes this seam is allowed to call ─────────────────────

export function studioAttemptBindingReadPath(projectId: string): string {
  return `/api/projects/${encodeURIComponent(
    projectId,
  )}/application-ontology-generation/attempt-binding`
}

export function studioArtifactValidationObservationAppendPath(
  projectId: string,
): string {
  return `/api/projects/${encodeURIComponent(
    projectId,
  )}/application-evidence/observations/artifact-validation`
}

export function studioArtifactPersistenceVerifyPath(projectId: string): string {
  return `/api/projects/${encodeURIComponent(
    projectId,
  )}/application-evidence/verifications/artifact-persistence`
}

// ─── The attached bound evidence (the resolver port's answer) ───────────

/**
 * Where a bound generation's evidence rides on the in-memory artifact:
 * `artifact.metadata[APPLICATION_ARTIFACT_BOUND_EVIDENCE_ATTACHMENT_KEY]`.
 * In-memory ONLY — the bundle carries raw bytes (`Uint8Array`), which do not
 * survive JSON persistence and are never meant to.
 */
export const APPLICATION_ARTIFACT_BOUND_EVIDENCE_ATTACHMENT_KEY =
  'cogoApplicationArtifactBoundEvidence'

/**
 * Everything the sealed admission needs that the artifact object itself does
 * not carry: the observation inputs (minus the artifact, supplied by
 * `admit()`'s own argument) plus the two post-model stage refs.
 * Derived from the observation seam's OWN input type, so a field the
 * observer gains or loses changes this bundle at compile time.
 */
export interface StudioApplicationArtifactBoundEvidence
  extends Omit<ObserveOntologyGeneratedApplicationInput, 'artifact'> {
  modelInvocationRef: ContentRef
  outputManifestRef: ContentRef
}

const BOUND_EVIDENCE_KEYS = [
  'applicationPackageBytes',
  'artifactBytes',
  'binding',
  'manifest',
  'requiredCapabilitySlice',
  'requiredApplicationTopologySlice',
  'compiledOntology',
  'validatorRef',
  'modelInvocationRef',
  'outputManifestRef',
] as const satisfies readonly (keyof StudioApplicationArtifactBoundEvidence)[]

type _BoundEvidenceKeysAreComplete = Exclude<
  keyof StudioApplicationArtifactBoundEvidence,
  (typeof BOUND_EVIDENCE_KEYS)[number]
> extends never
  ? true
  : never
const _boundEvidenceKeysAreComplete: _BoundEvidenceKeysAreComplete = true
void _boundEvidenceKeysAreComplete

/**
 * Three answers, not two: `malformed` exists so a corrupted attachment is
 * refused fail-closed instead of silently riding the unbound (`null`) lane.
 */
export type StudioApplicationArtifactBoundEvidenceResolution =
  | { kind: 'unbound' }
  | { kind: 'malformed' }
  | { kind: 'bound'; evidence: StudioApplicationArtifactBoundEvidence }

/**
 * SYNCHRONOUS on purpose: a resolver that cannot await cannot do transport,
 * so "unbound costs zero network" is a property of the type, not a promise.
 */
export type StudioApplicationArtifactBoundEvidenceResolver = (input: {
  projectId: string
  artifact: LoadedApp
}) => StudioApplicationArtifactBoundEvidenceResolution

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

/**
 * Brand check rather than `instanceof`: a `Uint8Array` minted in another
 * realm (an iframe's, a test environment's `TextEncoder`) is still bytes.
 */
function isByteArray(value: unknown): value is Uint8Array {
  return Object.prototype.toString.call(value) === '[object Uint8Array]'
}

/**
 * Structural guard for the attached binding — exactly the members the seam's
 * own consumers read: the three scope refs and the ontology-package ref the
 * sealed admission and the Stage-3 routes cross-check
 * (`readPresentedBindingIdentity`), the validator identity the observer
 * compares, and the nested KG binding the routes pin to the persisted
 * attempt. Deeper validation is the platform verifier's job, and staying at
 * this depth means a binding the routes would accept is never refused here
 * for a field nobody reads.
 */
function isAttachedBindingShape(value: unknown): boolean {
  return (
    isPlainRecord(value) &&
    isApplicationEvidenceContentRef(value.profileSelectionRef) &&
    isApplicationEvidenceContentRef(value.projectScopeRef) &&
    isApplicationEvidenceContentRef(value.actorScopeRef) &&
    isApplicationEvidenceContentRef(value.applicationOntologyPackageRef) &&
    isApplicationEvidenceContentRef(value.validatorRef) &&
    isPlainRecord(value.kgPreflight)
  )
}

/**
 * The PRODUCTION resolver: reads the artifact's own attachment slot. Pure,
 * synchronous, in-memory — no fetch, no store, no clock.
 */
export function resolveAttachedApplicationArtifactBoundEvidence(input: {
  projectId: string
  artifact: LoadedApp
}): StudioApplicationArtifactBoundEvidenceResolution {
  const metadata = (input.artifact as { metadata?: unknown }).metadata
  const attached = isPlainRecord(metadata)
    ? metadata[APPLICATION_ARTIFACT_BOUND_EVIDENCE_ATTACHMENT_KEY]
    : undefined
  if (attached === undefined) return { kind: 'unbound' }
  if (!hasExactApplicationEvidenceKeys(attached, BOUND_EVIDENCE_KEYS)) {
    return { kind: 'malformed' }
  }
  if (
    !isByteArray(attached.applicationPackageBytes) ||
    !isByteArray(attached.artifactBytes) ||
    !isAttachedBindingShape(attached.binding) ||
    !isPlainRecord(attached.manifest) ||
    !isPlainRecord(attached.requiredCapabilitySlice) ||
    !isPlainRecord(attached.requiredApplicationTopologySlice) ||
    !isPlainRecord(attached.compiledOntology) ||
    !isApplicationEvidenceContentRef(attached.validatorRef) ||
    !isApplicationEvidenceContentRef(attached.modelInvocationRef) ||
    !isApplicationEvidenceContentRef(attached.outputManifestRef)
  ) {
    return { kind: 'malformed' }
  }
  // Key-closure and every ref/byte member this seam's consumers read are
  // verified above; the record BODIES are validated by the invariant
  // evaluator and re-derived by the platform verifier downstream.
  return {
    kind: 'bound',
    evidence: attached as unknown as StudioApplicationArtifactBoundEvidence,
  }
}

// ─── The composed seam ──────────────────────────────────────────────────

export interface StudioApplicationArtifactPersistenceServerSeamDeps {
  resolveBoundEvidence: StudioApplicationArtifactBoundEvidenceResolver
  /** Studio session headers for all four legs (the routes authenticate first). */
  authHeaders: () => Promise<Record<string, string>>
  /** Injectable clock for tests; production uses the real one. */
  now?: () => string
}

function refusedUnavailable(): ApplicationArtifactPersistenceAdmissionDecision {
  return { kind: 'refused', code: 'ADMISSION_UNAVAILABLE' }
}

interface ServerBoundIdentity {
  series: ApplicationSeriesBinding
  attempt: ApplicationAttemptBinding
}

/**
 * Leg 1 — the Stage-1 persisted `{series, attempt}`, the two identities the
 * browser must never assert itself. `null` for ANY non-200, non-closed or
 * incoherent answer; the caller refuses `ADMISSION_UNAVAILABLE`.
 */
async function readServerBoundIdentity(
  projectId: string,
  headers: Record<string, string>,
): Promise<ServerBoundIdentity | null> {
  let payload: unknown
  try {
    const response = await fetch(studioAttemptBindingReadPath(projectId), {
      headers,
    })
    if (!response || response.status !== 200) return null
    payload = await response.json()
  } catch {
    return null
  }
  if (!hasExactApplicationEvidenceKeys(payload, ['series', 'attempt'])) {
    return null
  }
  const series = payload.series
  const attempt = payload.attempt
  if (!isApplicationSeriesBindingShape(series)) return null
  if (!isApplicationAttemptBindingShape(attempt)) return null
  // The attempt PINS its series: a pair from two different rows is not the
  // persisted binding, whatever each half looks like alone.
  if (
    attempt.seriesId !== series.seriesId ||
    attempt.seriesHash !== series.seriesHash
  ) {
    return null
  }
  return { series, attempt }
}

const APPEND_ANSWER_KEYS = [
  'artifactValidationObservationRef',
  'artifactValidationReceiptRef',
  'receiptChainHeadRef',
] as const satisfies readonly (keyof StudioArtifactValidationObservationAppendResult)[]

type _AppendAnswerKeysAreComplete = Exclude<
  keyof StudioArtifactValidationObservationAppendResult,
  (typeof APPEND_ANSWER_KEYS)[number]
> extends never
  ? true
  : never
const _appendAnswerKeysAreComplete: _AppendAnswerKeysAreComplete = true
void _appendAnswerKeysAreComplete

/**
 * Leg 2 — the observation append. The binding sent is the OBSERVATION'S own
 * (the exact binding the observation was sealed from — the route re-pins it
 * to the persisted attempt), and the stage hashes are read from the
 * observation's computed refs plus the two attached post-model refs. A
 * failure THROWS: the sealed admission maps any append throw to
 * `ADMISSION_UNAVAILABLE` at its own step boundary.
 */
async function appendObservationThroughStudioRoute(input: {
  projectId: string
  observation: ApplicationArtifactValidationObservation
  modelInvocationRef: ContentRef
  outputManifestRef: ContentRef
  headers: Record<string, string>
}): Promise<StudioArtifactValidationObservationAppendResult> {
  const { observation } = input
  const response = await fetch(
    studioArtifactValidationObservationAppendPath(input.projectId),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...input.headers },
      body: JSON.stringify({
        observation,
        binding: observation.binding,
        stage: {
          stage: 'artifact',
          applicationPackageHash: observation.applicationPackageRef.contentHash,
          artifactHash: observation.artifactRef.contentHash,
          modelInvocationHash: input.modelInvocationRef.contentHash,
          outputManifestHash: input.outputManifestRef.contentHash,
        },
      }),
    },
  )
  if (!response || response.status !== 200) {
    throw new Error('artifact-validation observation append refused')
  }
  const payload: unknown = await response.json()
  if (
    !hasExactApplicationEvidenceKeys(payload, APPEND_ANSWER_KEYS) ||
    !isApplicationEvidenceContentRef(payload.artifactValidationObservationRef) ||
    !isApplicationEvidenceContentRef(payload.artifactValidationReceiptRef) ||
    !isApplicationEvidenceContentRef(payload.receiptChainHeadRef)
  ) {
    throw new Error('artifact-validation observation append answered malformed')
  }
  return {
    artifactValidationObservationRef: payload.artifactValidationObservationRef,
    artifactValidationReceiptRef: payload.artifactValidationReceiptRef,
    receiptChainHeadRef: payload.receiptChainHeadRef,
  }
}

/**
 * Closed parse of the verify proxy's relayed canonical decision — the same
 * two branches, key closures and code membership as the Pages-side
 * `parseArtifactPersistenceAdmissionDecision`, against the admission
 * client's exported (completeness-proved) code list. An `admitted` with an
 * extra field, or a `refused` with an invented code, is NOT a decision.
 */
function parseRelayedArtifactPersistenceAdmissionDecision(
  value: unknown,
  admissionRefusalCodes: readonly string[],
): ApplicationArtifactPersistenceAdmissionDecision | null {
  if (!isPlainRecord(value)) return null
  if (value.kind === 'admitted') {
    if (
      !hasExactApplicationEvidenceKeys(value, [
        'kind',
        'eligibilityRef',
        'admissionRef',
      ]) ||
      !isApplicationEvidenceContentRef(value.eligibilityRef) ||
      !isApplicationEvidenceContentRef(value.admissionRef)
    ) {
      return null
    }
    return {
      kind: 'admitted',
      eligibilityRef: value.eligibilityRef,
      admissionRef: value.admissionRef,
    }
  }
  if (value.kind === 'refused') {
    if (
      !hasExactApplicationEvidenceKeys(value, ['kind', 'code']) ||
      typeof value.code !== 'string' ||
      !admissionRefusalCodes.includes(value.code)
    ) {
      return null
    }
    return {
      kind: 'refused',
      code: value.code as ApplicationEvidenceAdmissionRefusalCode,
    }
  }
  return null
}

/**
 * Leg 4 — verification. Operational failure answers the canonical
 * `ADMISSION_UNAVAILABLE` refusal (never a throw), matching the platform
 * adapter's own posture; a platform verdict travels back UNCHANGED.
 */
async function verifyThroughStudioRoute(input: {
  projectId: string
  request: ApplicationArtifactPersistenceAdmissionRequest
  headers: Record<string, string>
  admissionRefusalCodes: readonly string[]
}): Promise<ApplicationArtifactPersistenceAdmissionDecision> {
  try {
    const response = await fetch(
      studioArtifactPersistenceVerifyPath(input.projectId),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...input.headers },
        body: JSON.stringify(input.request),
      },
    )
    if (!response || response.status !== 200) return refusedUnavailable()
    const decision = parseRelayedArtifactPersistenceAdmissionDecision(
      await response.json(),
      input.admissionRefusalCodes,
    )
    return decision ?? refusedUnavailable()
  } catch {
    return refusedUnavailable()
  }
}

export function createStudioApplicationArtifactPersistenceServerSeam(
  deps: StudioApplicationArtifactPersistenceServerSeamDeps,
): StudioApplicationArtifactPersistenceServerSeam {
  return {
    async admit(input) {
      // Pure, in-memory, synchronous: the ordinary (unbound) lane pays no
      // transport, no dynamic import, no header derivation.
      const resolution = deps.resolveBoundEvidence(input)
      if (resolution.kind === 'unbound') return null
      if (resolution.kind === 'malformed') return refusedUnavailable()
      const evidence = resolution.evidence

      // From here the generation IS bound: every failure refuses, never null.
      try {
        const headers = await deps.authHeaders()
        const identity = await readServerBoundIdentity(input.projectId, headers)
        if (!identity) return refusedUnavailable()

        // The sealed order and its code list, loaded on the bound path only
        // (the same entry-chunk discipline as the bridge's own deferred
        // `register-studio-tools` import).
        const {
          ADMISSION_REFUSAL_CODES,
          admitOntologyGeneratedApplicationPersistence,
        } = await import('./application-evidence-admission-client')

        const { modelInvocationRef, outputManifestRef, ...observationInputs } =
          evidence
        return await admitOntologyGeneratedApplicationPersistence({
          projectId: input.projectId,
          observation: { artifact: input.artifact, ...observationInputs },
          series: identity.series,
          attempt: identity.attempt,
          modelInvocationRef,
          outputManifestRef,
          now: (deps.now ?? (() => new Date().toISOString()))(),
          port: {
            appendArtifactValidationObservation: (observation) =>
              appendObservationThroughStudioRoute({
                projectId: input.projectId,
                observation,
                modelInvocationRef,
                outputManifestRef,
                headers,
              }),
            verifyArtifactPersistenceEligibility: (request) =>
              verifyThroughStudioRoute({
                projectId: input.projectId,
                request,
                headers,
                admissionRefusalCodes: ADMISSION_REFUSAL_CODES,
              }),
          },
          authHeaders: headers,
        })
      } catch {
        return refusedUnavailable()
      }
    },
  }
}

/**
 * The production composition the bridge bootstrap installs: the attachment
 * resolver above plus the Studio session credential. The auth import is
 * deferred with the other heavy legs — an unbound admit derives no headers.
 */
export function createProductionStudioApplicationArtifactPersistenceServerSeam(): StudioApplicationArtifactPersistenceServerSeam {
  return createStudioApplicationArtifactPersistenceServerSeam({
    resolveBoundEvidence: resolveAttachedApplicationArtifactBoundEvidence,
    authHeaders: async () => {
      const { getStudioAccessToken } = await import('@/services/auth-provider')
      const token = await getStudioAccessToken()
      const headers: Record<string, string> = {}
      if (token) headers.Authorization = `Bearer ${token}`
      return headers
    },
  })
}
